import React, { useCallback, useReducer } from 'react';
import Row from '@amzn/meridian/row';
import Box from '@amzn/meridian/box';
import MeridianControlGroup from '@amzn/meridian/control-group';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
// react-intl v2 has no hooks — use the injectIntl HOC (matches v1 + the app's react-intl 2.9).
import { FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl';
import { useDeviceLayout } from '../common/context/DeviceLayoutContext';
import FilterExpander from '../common/FilterExpander';
import FilterTags from './FilterTags';

// Meridian 5's ControlGroup renders its group heading from the `label` prop (it emits
// `<legend>{label}</legend>`; its propTypes define `label` and NO `legend`). The deployed app
// pins @amzn/meridian ^5, so the title MUST be passed via `label` — this matches the extraction
// contract (ui.yaml: active-control-group props.label). (Meridian 8 later renamed this to
// `legend`, which is why an 8-based sandbox masked the missing title — see bugfix-D495987189.)
// Its bundled .d.ts types the component as a class exposing a subset of props, so we cast to a
// loosely-typed component to keep the `label` prop.
const ControlGroup = MeridianControlGroup as unknown as React.ComponentType<{
  label?: string;
  name?: string;
  onChange?: (data: string[]) => void;
  value?: string[];
  children?: React.ReactNode;
}>;

// --- State (ported from AssociateFilterReducer): the shared active-state filter ---
type FilterAction =
  | { type: 'SELECT_STATE'; state: string[] }
  | { type: 'REMOVE_STATE'; state: string }
  | { type: 'REMOVE_ALL_STATES' };

function filterReducer(states: string[], action: FilterAction): string[] {
  switch (action.type) {
    case 'SELECT_STATE':
      return action.state;
    case 'REMOVE_STATE':
      return states.filter((s) => s !== action.state);
    case 'REMOVE_ALL_STATES':
      return [];
    default:
      return states;
  }
}

export interface AssociateFilterProps {
  /** Controlled selection. Omit to let the component own it internally (standalone use). */
  states?: string[];
  /** Emitted with the full new selection when controlled. */
  onChange?: (states: string[]) => void;
}

/**
 * AssociateFilter — active-state (Yes/No) filter for the associate roster (React + Meridian).
 *
 * Design-system continuity: same Meridian components as v1 (ControlGroup/Checkbox/Link/Row/
 * Column/Box). The v1 AssociateFilterContext + reducer is preserved as a local useReducer; the
 * composer (Associate) may instead control it via `states`/`onChange` (Rule 10: composer owns
 * cross-screen shared state). Mobile wraps in a collapsible section; desktop renders inline.
 */
const AssociateFilter: React.FC<AssociateFilterProps & InjectedIntlProps> = ({
  states: controlled,
  onChange,
  intl,
}) => {
  const { isMobile, spacingInset_shorthand3 } = useDeviceLayout();
  const [internal, dispatch] = useReducer(filterReducer, []);

  const isControlled = controlled !== undefined;
  const states = isControlled ? (controlled as string[]) : internal;

  const apply = useCallback(
    (next: string[]) => {
      if (isControlled) onChange?.(next);
      else dispatch({ type: 'SELECT_STATE', state: next });
    },
    [isControlled, onChange]
  );

  const isFilterSet = states.length !== 0;
  const clearAllFilters = () => apply([]);
  const removeState = (state: string) =>
    apply(states.filter((s) => s !== state));

  const renderActiveAssociateControlGroup = () => (
    <ControlGroup
      label={intl.formatMessage({
        id: 'associate.active',
        defaultMessage: 'Active',
      })}
      name="state"
      onChange={(data: string[]) => apply(data)}
      value={states}
    >
      <Checkbox key="yes" value="Yes">
        <FormattedMessage id="associate.yes" defaultMessage="Yes" />
      </Checkbox>
      <Checkbox key="no" value="No">
        <FormattedMessage id="associate.no" defaultMessage="No" />
      </Checkbox>
    </ControlGroup>
  );

  const renderFilterSection = () => (
    <React.Fragment>
      {renderActiveAssociateControlGroup()}
      {isFilterSet && (
        <Row width="100%" spacingInset="small none none none">
          <FilterTags states={states} onRemove={removeState} />
          <Link onClick={clearAllFilters} type="secondary">
            <FormattedMessage
              id="linehaul_filter.clear_all"
              defaultMessage="Clear all"
            />
          </Link>
        </Row>
      )}
    </React.Fragment>
  );

  return (
    <Column>
      <Box>
        {isMobile ? (
          // Mobile: collapse the filter into a FilterExpander (collapsed by default), matching v1
          // and the mobile-filter-expander contract. This is the fix for D496001686 — the migrated
          // component previously rendered this Column inline, so mobile looked identical to desktop.
          <FilterExpander>
            <Column
              alignmentHorizontal="stretch"
              alignmentVertical="center"
              spacingInset="small medium"
              backgroundColor="secondary"
            >
              {renderFilterSection()}
            </Column>
          </FilterExpander>
        ) : (
          <Row
            width="100%"
            spacingInset={spacingInset_shorthand3}
            backgroundColor="secondary"
          >
            {renderActiveAssociateControlGroup()}
          </Row>
        )}
      </Box>
      {!isMobile && (
        <Row width="100%">
          <FilterTags states={states} onRemove={removeState} />
          {isFilterSet ? (
            <Link onClick={clearAllFilters} type="secondary">
              <FormattedMessage
                id="linehaul_filter.clear_all"
                defaultMessage="Clear all"
              />
            </Link>
          ) : null}
        </Row>
      )}
    </Column>
  );
};

export default injectIntl(AssociateFilter);
